import { useRef, useEffect, useState } from 'react';
import ReactSignatureCanvas from 'react-signature-canvas';
import { useTranslation } from 'react-i18next';
import styles from './SignatureCanvas.module.scss';

interface SignatureCanvasProps {
  /** Called with a base64 PNG data URL whenever the drawn signature changes, or '' when cleared. */
  onCapture: (base64Png: string) => void;
  invalid?: boolean;
}

/** Strips the `data:image/png;base64,` prefix so the value matches DocumentDtoOut.contentBase64. */
export const stripSignatureDataUrl = (dataUrl: string): string => {
  const commaIndex = dataUrl.indexOf(',');
  return commaIndex >= 0 ? dataUrl.substring(commaIndex + 1) : dataUrl;
};

/**
 * Presentational signature pad shared by newOpenFundRequest/components/SignaturePadField (which
 * wraps it in a react-hook-form Controller) and SPR-1054's post-creation signature/approval
 * dialogs, which aren't forms with a submit step and call onCapture directly.
 */
const SignatureCanvas = ({ onCapture, invalid }: SignatureCanvasProps) => {
  const { t } = useTranslation();
  const sigRef = useRef<ReactSignatureCanvas | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [canvasWidth, setCanvasWidth] = useState(600);

  useEffect(() => {
    const updateSize = () => {
      if (wrapperRef.current) {
        setCanvasWidth(wrapperRef.current.clientWidth);
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return (
    // ph-mask: חתימה היא מידע ביומטרי-זהותי - ממוסך בהקלטות PostHog (HAT-1081).
    <div className={`${styles.wrapper} ph-mask`} ref={wrapperRef}>
      <div className={`${styles.canvasWrapper} ${invalid ? styles.invalid : ''}`}>
        <ReactSignatureCanvas
          ref={(r) => {
            sigRef.current = r;
          }}
          canvasProps={{
            width: canvasWidth,
            height: 180,
            className: styles.canvas,
          }}
          onEnd={() => {
            if (sigRef.current) {
              onCapture(sigRef.current.toDataURL('image/png'));
            }
          }}
        />
      </div>
      <button
        type="button"
        className={styles.clearButton}
        onClick={() => {
          sigRef.current?.clear();
          onCapture('');
        }}
      >
        {t('openFundRequest.fields.clearSignature')}
      </button>
    </div>
  );
};

export default SignatureCanvas;
